// Daily challenges (§7.4): 3 per UTC day from a pool of 18, drawn by a date seed; each is worth
// 150 XP and 50 Bonk Bucks. Progress is tracked client-side from sim events involving the local
// slot (live, for toasts) and reconciled with the server's match stats on `match:end`.
// Pure: no DOM, no storage. `ChallengeTracker` turns events into per-challenge progress deltas.
import { NO_SLOT, TICK_HZ, TUNE } from '@shared/constants';
import { EV, KoFlag, type SimEvent } from '@shared/events';
import { Cause, MapId, ModeId } from '@shared/ids';
import { hashString, seedRng, shuffle, type RngHolder } from '@shared/rng';
import type { RoundStats } from '@shared/types';

export interface ChallengeDef {
  /** Pool index 0–17 (the §7.4 table's # minus one). */
  id: number;
  text: string;
  goal: number;
  /** Short stat label for progress chips ("12/15 shoves"). */
  unit: string;
  /** The RoundStats field that counts the same thing (reconciled at match end), if any. */
  stat?: keyof RoundStats;
  /** Divides the stat (crown ticks → seconds). */
  statDiv?: number;
}

/** The 18-challenge pool, in §7.4 order. */
export const CHALLENGES: readonly ChallengeDef[] = [
  { id: 0, text: 'KO 2 beans with a Banana Peel', goal: 2, unit: 'peel KOs', stat: 'peelKos' },
  { id: 1, text: 'Win a Hot Potato round', goal: 1, unit: 'wins' },
  { id: 2, text: 'Hand-stomp a ledge-hanger', goal: 1, unit: 'stomps', stat: 'stomps' },
  { id: 3, text: 'Land 15 shoves', goal: 15, unit: 'shoves', stat: 'shovesLanded' },
  { id: 4, text: 'Throw 3 beans to a KO', goal: 3, unit: 'throw KOs', stat: 'throwKos' },
  { id: 5, text: 'Win a Crown Grab round', goal: 1, unit: 'wins' },
  { id: 6, text: 'Make 3 CLUTCH! saves', goal: 3, unit: 'clutches', stat: 'clutches' },
  { id: 7, text: 'Get 2 ghost KOs', goal: 2, unit: 'ghost KOs', stat: 'ghostKos' },
  { id: 8, text: 'Get a REVENGE', goal: 1, unit: 'revenges', stat: 'revenges' },
  { id: 9, text: 'Claim a BOUNTY', goal: 1, unit: 'bounties', stat: 'bounties' },
  { id: 10, text: 'KO a bean with a Bonk Bomb', goal: 1, unit: 'bomb KOs', stat: 'bombKos' },
  { id: 11, text: 'Survive a fall with a Balloon', goal: 1, unit: 'saves', stat: 'balloonSaves' },
  { id: 12, text: 'Win 3 CLANGs', goal: 3, unit: 'CLANGs', stat: 'clashesWon' },
  { id: 13, text: 'Honk 30 times', goal: 30, unit: 'honks', stat: 'honks' },
  { id: 14, text: 'Place top 3 in a Golden Round', goal: 1, unit: 'top 3s' },
  { id: 15, text: 'KO 2 beans in one round on Spin Cycle', goal: 2, unit: 'KOs in a round' },
  { id: 16, text: 'Hold the crown for 15 s total', goal: 15, unit: 's', stat: 'crownTicks', statDiv: TICK_HZ },
  { id: 17, text: 'Escape 3 grabs', goal: 3, unit: 'escapes', stat: 'grabsEscaped' },
];

/** UTC calendar date `YYYY-MM-DD` (challenges and the shop rotate at UTC midnight). */
export function utcDate(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The 3 challenge ids for a UTC date: distinct, deterministic, seeded by the date (§7.4). */
export function dailyChallengeIds(date: string): number[] {
  const h: RngHolder = { rng: seedRng(hashString(`challenges:${date}`)) };
  const ids = shuffle(h, CHALLENGES.map((c) => c.id));
  return ids.slice(0, TUNE.progression.challenges.perDay).sort((a, b) => a - b);
}

/** Round context the tracker needs (from `round:init` / `round:end`). */
export interface RoundContext { mapId: MapId; modeId: ModeId; golden: boolean; team: number }

/**
 * Live challenge counting for one local player. Feed it the round context, every non-replay sim
 * event and the round results; it reports progress deltas per challenge id through `onDelta`.
 * It keeps per-match tallies so `reconcile()` can top up from the server's match stats.
 */
export class ChallengeTracker {
  private round: RoundContext | null = null;
  private crownSince = -1;
  private crownFrac = 0;
  private roundKos = 0;
  /** Live counts this match, per challenge id. */
  readonly matchCounts = new Array<number>(CHALLENGES.length).fill(0);

  constructor(private readonly onDelta: (id: number, delta: number, absolute?: number) => void) {}

  /** A new scoring round started (null for lobby / podium worlds: nothing counts there). */
  beginRound(ctx: RoundContext | null): void {
    this.round = ctx;
    this.crownSince = -1;
    this.roundKos = 0;
  }

  beginMatch(): void {
    this.matchCounts.fill(0);
    this.crownFrac = 0;
  }

  get active(): boolean {
    return this.round !== null;
  }

  private add(id: number, n = 1): void {
    if (n <= 0) return;
    this.matchCounts[id] += n;
    this.onDelta(id, n);
  }

  private stopCrown(tick: number): void {
    if (this.crownSince < 0) return;
    const sec = Math.max(0, tick - this.crownSince) / TICK_HZ + this.crownFrac;
    this.crownSince = -1;
    const whole = Math.floor(sec);
    this.crownFrac = sec - whole;
    this.add(16, whole);
  }

  /** One sim event (never a replayed one) for local slot `me`. */
  event(ev: SimEvent, me: number): void {
    const r = this.round;
    if (!r || me === NO_SLOT) return;
    switch (ev.type) {
      case EV.KO: {
        if (ev.a === me) this.stopCrown(ev.tick);
        if (ev.b !== me || ev.a === me || ev.d & (KoFlag.SELF | KoFlag.TEAMKILL)) break;
        if (ev.d & KoFlag.GHOST_KO) this.add(7);
        if (ev.d & KoFlag.REVENGE) this.add(8);
        if (ev.d & KoFlag.BOUNTY) this.add(9);
        if (ev.c === Cause.PEEL) this.add(0);
        else if (ev.c === Cause.THROW) this.add(4);
        else if (ev.c === Cause.BOMB) this.add(10);
        if (r.mapId === MapId.SPIN) {
          this.roundKos++;
          // Best round so far: report the absolute value; the service keeps the max.
          this.matchCounts[15] = Math.max(this.matchCounts[15], this.roundKos);
          this.onDelta(15, 0, this.roundKos);
        }
        break;
      }
      case EV.HIT: if (ev.a === me && ev.c === Cause.SHOVE && ev.b !== NO_SLOT) this.add(3); break;
      case EV.STOMP: if (ev.a === me) this.add(2); break;
      case EV.TEETER_SAVE: if (ev.a === me && ev.c === 1) this.add(6); break;
      case EV.BALLOON_SAVE: if (ev.a === me) this.add(11); break;
      case EV.CLASH: if (ev.a === me || ev.b === me) this.add(12); break;
      case EV.HONK: if (ev.a === me) this.add(13); break;
      case EV.ESCAPE: if (ev.a === me) this.add(17); break;
      case EV.CROWN_PICK:
        if (ev.a === me) this.crownSince = ev.tick;
        else this.stopCrown(ev.tick);
        break;
      case EV.CROWN_POP: if (ev.a === me) this.stopCrown(ev.tick); break;
      case EV.CROWN_LOST: case EV.ROUND_OVER: this.stopCrown(ev.tick); break;
      default: break;
    }
  }

  /** Round results (`round:end`): round wins per mode and Golden Round placings. */
  roundEnd(place: number, winner: boolean): void {
    const r = this.round;
    if (!r) return;
    if (winner && r.modeId === ModeId.HOT_POTATO) this.add(1);
    if (winner && r.modeId === ModeId.CROWN_GRAB) this.add(5);
    if (r.golden && place >= 1 && place <= 3) this.add(14);
  }

  /**
   * Match end: the server's totals for the local slot. Any stat-backed challenge whose server
   * count exceeds what was seen live gets the difference (dropped or late events).
   */
  reconcile(stats: RoundStats | undefined): void {
    if (!stats) return;
    for (const def of CHALLENGES) {
      if (!def.stat) continue;
      const v = Math.floor((stats[def.stat] ?? 0) / (def.statDiv ?? 1));
      const delta = v - this.matchCounts[def.id];
      if (delta > 0) this.add(def.id, delta);
    }
  }
}
